import { INGChartProps } from "../../library/NGFieldExtensions";
import { useComputed, useSignal } from "@preact/signals-react";
import type { HighchartsReactRefObject } from "highcharts-react-official";
import { useRef, useEffect } from "react";
import { setupLocalState } from "../../library/dataService";
import { getTestId, isNullOrEmpty, splitCamelCase } from "../../library/utils";

interface IChartSeriesBuildInput {
  rows?: any[];
  transposeRowNames: string[];
  transposeRowValueFrom: string;
  colors?: string[] | null;
  transposeRowLabel?: string;
}

function chooseColorBasedOnName(name?: string) {
  // Basic logic to assign color, could be enhanced to be more dynamic
  const baseColors = ["#EC2500", "#ECE100", "#EC9800", "#9EDE00", "#00C0EF", "#00ECB4", "#0099EC"];
  if (!name) return baseColors[0]
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return baseColors[Math.abs(hash) % baseColors.length];
}

function transformDataForTreeMap(input: IChartSeriesBuildInput) {
  const { rows, transposeRowNames, transposeRowValueFrom, colors: inputColors } = input;
  const dataMap = new Map();
  const colors = new Map();

  rows?.forEach((row, index) => {
    const name = row[transposeRowNames];
    const value = parseFloat(row[transposeRowValueFrom]); // Ensure value is treated as a number

    if (isNullOrEmpty(name)) return; // Skip empty names, could be enhanced to use a default name

    if (dataMap.has(name)) {
      // If the name already exists, sum the values
      dataMap.set(name, dataMap.get(name) + value);
    } else {
      // Otherwise, add the new name and value
      dataMap.set(name, value);
    }

    if (row["Color"] && !colors.has(name)) colors.set(name, row["Color"]);
    else if (inputColors?.[index]) colors.set(name, inputColors[index]);
  });

  // Convert the map to the format Highcharts expects
  const transformedData = Array.from(dataMap, ([name, value]) => ({
    name,
    value,
    color: colors.get(name) ?? chooseColorBasedOnName(name), // Placeholder for a color assignment function
  }));

  return [
    {
      layoutAlgorithm: "squarified",
      data: transformedData,
    },
  ];
}

function transformDataForBarChart(input: IChartSeriesBuildInput) {
  const { rows, transposeRowNames, transposeRowValueFrom, colors } = input;

  // Accumulate results in a map to merge duplicates
  const resultsMap = {};

  rows?.forEach((row, index) => {
    transposeRowNames.forEach((transposeRowName) => {
      if (Object.prototype.hasOwnProperty.call(row, transposeRowName)) {
        const rowKey = row[transposeRowName];
        const rowValue = row[transposeRowValueFrom];
        if (!resultsMap[rowKey]) {
          // If it's not inside yet, initialize it
          resultsMap[rowKey] = {
            name: rowKey,
            data: [rowValue],
            color: row["Color"] ?? colors?.[index] ?? chooseColorBasedOnName(rowKey), // Placeholder for a color assignment function
            dataLabels: { enabled: false },
          };
        } else {
          // If it's already inside, add the rowValue to the existing data
          resultsMap[rowKey].data[0] += rowValue;
        }
      }
    });
  });

  return Object.values(resultsMap);
}

function transformDataForBulletChart(input: IChartSeriesBuildInput) {
  const { rows, transposeRowNames, transposeRowValueFrom, colors } = input;
  const resultsMap: any[] = [];
  // expect result for rows to be array
  // it should contain an Id or add transposeRowNames
  // for each transposeRowName it will generate a new bullet chart with target
  // for each record of different transposeRowName it can have a property called Target to add target
  // transposeRowName will be series name
  // value can be reached from transposeRowValueFrom

  const rowNames = transposeRowNames.length ? transposeRowNames : ["Id"];

  rows?.forEach((row, index) => {
    rowNames.forEach((transposeRowName) => {
      if (Object.prototype.hasOwnProperty.call(row, transposeRowName)) {
        const rowKey = row[transposeRowName];
        const rowValue = row[transposeRowValueFrom ?? "Value"];
        const rowTarget = row["Target"];
        resultsMap.push({
          name: rowKey,
          color: row["Color"] ?? colors?.[index] ?? undefined,
          data: [{ y: rowValue, target: rowTarget }],
        });
      }
    });
  });

  return resultsMap;
}

function transformDataForPieChart(input: IChartSeriesBuildInput) {
  const { rows, transposeRowNames, transposeRowValueFrom, colors, transposeRowLabel } = input;

  const resultsMap = {};

  rows?.forEach((row, index) => {
    transposeRowNames?.forEach((transposeRowName) => {
      if (Object.prototype.hasOwnProperty.call(row, transposeRowName)) {
        const rowKey = row[transposeRowName];
        const rowValue = row[transposeRowValueFrom];
        if (!resultsMap[rowKey]) {
          resultsMap[rowKey] = {
            name: rowKey,
            y: rowValue,
            color: row["Color"] ?? colors?.[index] ?? undefined, // Placeholder for a color assignment function
          };
        } else {
          resultsMap[rowKey].y += rowValue;
        }
      }
    });
  });

  return [
    {
      name: transposeRowLabel ?? splitCamelCase(transposeRowValueFrom),
      colorByPoint: true,
      data: Object.values(resultsMap),
    },
  ];
}

function transformDataDefault(input) {
  const { rows, transposeRowNames, transposeRowValueFrom, colors } = input;

  // Accumulate results in a map to merge duplicates
  const resultsMap = {};

  rows.forEach((row, index) => {
    transposeRowNames.forEach((transposeRowName) => {
      if (row.hasOwnProperty(transposeRowName)) {
        const rowKey = row[transposeRowName];
        const rowValue = row[transposeRowValueFrom];
        if (!resultsMap[transposeRowName]) {
          // If it's not inside yet, initialize it
          resultsMap[transposeRowName] = {
            name: transposeRowName,
            data: [[rowKey, rowValue]],
            color: row["Color"] ?? colors[index] ?? chooseColorBasedOnName(rowKey), // Placeholder for a color assignment function
            // dataLabels: { enabled: false },
          };
        } else {
          // If it's already inside, add the rowValue to the existing data
          resultsMap[transposeRowName].data.push([rowKey, rowValue]);
        }
      }
    });
  });

  const result = Object.values(resultsMap);
  console.log("transformDataDefault result", result);
  return result;
}

function transformDataForSunburstChart(input: IChartSeriesBuildInput) {
  const { rows, transposeRowNames, transposeRowValueFrom, colors, transposeRowLabel } = input;

  const resultsMap = {};

  rows?.forEach((row, index) => {
    transposeRowNames?.forEach((transposeRowName) => {
      if (Object.prototype.hasOwnProperty.call(row, transposeRowName)) {
        const rowKey = row[transposeRowName];
        const rowValue = row[transposeRowValueFrom];
        if (!resultsMap[rowKey]) {
          resultsMap[rowKey] = {
            name: rowKey,
            value: rowValue,
            color: row["Color"] ?? colors?.[index] ?? undefined,
          };
        } else {
          resultsMap[rowKey].value += rowValue;
        }
      }
    });
  });

  return [
    {
      name: transposeRowLabel ?? splitCamelCase(transposeRowValueFrom),
      colorByPoint: true,
      data: Object.values(resultsMap),
    },
  ];
}

export default function NGHighchart({ config, context }: INGChartProps) {
  console.log("~~NGHighchart", config);

  const testId = getTestId(config);

  const local = setupLocalState(
    config,
    {
      Rows: useSignal(config.Rows ?? []), // Rows is the data as passed in by the bindings
      TransposeRowsToSeries: useSignal(config.TransposeRowsToSeries ?? false),
      TransposeRowNames: useSignal(config.TransposeRowNames ?? ""),
      TransposeRowValueFrom: useSignal(config.TransposeRowValueFrom ?? ""),
      TransposeRowLabel: useSignal(config.TransposeRowLabel ?? undefined),
      Loading: useSignal(config.Loading ?? false),
      ChartMetadata: useSignal(config.ChartMetadata || {}),
      Visible: useSignal(config.Visible ?? true),
      Colors: useSignal(config.Colors ?? []),
      Height: useSignal(config.Height ?? null),
    },
    context
  );
  const modulesLoaded = useSignal(false);
  const HighchartsReactModule: any = useSignal(null);
  const HighchartsModule: any = useSignal(null);
  const tag = "NGHighchart";

  useEffect(() => {
    const loadModules = async () => {
      const { Highcharts, HighchartsReact } = await import("@/lib/highchart/highchart-bundle.es.js");
      HighchartsReactModule.value = HighchartsReact;
      HighchartsModule.value = Highcharts;
      modulesLoaded.value = true;
    };

    loadModules();
  }, []);

  const chartComponentRef = useRef<HighchartsReactRefObject>(null);

  const options = useComputed(() => {
    const chartConfig = {
      ...local.ChartMetadata.value,
      exporting: {
        enabled: false, // This disables the hamburger menu
      },
      credits: {
        enabled: false, // This removes the Highcharts credit link
      },
    };

    // Set followTouchMove to false for all charts in order to not block touch events
    chartConfig.tooltip = {
      ...chartConfig.tooltip,
      followTouchMove: false,
    };

    // check if local.Rows.value is an array
    if (!Array.isArray(local.Rows.value)) return chartConfig;
    if (!local.TransposeRowsToSeries.value) return chartConfig;

    const rows = [...local.Rows.value];
    const chartType = local.ChartMetadata.value?.chart?.type ?? "bar";

    const chartTypesSeries = {
      treemap: transformDataForTreeMap,
      bar: transformDataForBarChart,
      bullet: transformDataForBulletChart,
      pie: transformDataForPieChart,
      sunburst: transformDataForSunburstChart,
      default: transformDataDefault,
    };

    if (local.TransposeRowNames.value?.length || local.TransposeRowValueFrom.value || rows.length) {
      chartConfig.series = (chartTypesSeries[chartType] ?? chartTypesSeries.default)({
        rows,
        transposeRowNames: local.TransposeRowNames.value,
        transposeRowValueFrom: local.TransposeRowValueFrom.value,
        transposeRowLabel: local.TransposeRowLabel.value,
        colors: local.Colors.value,
      });
    }

    if (local.Height.value) {
      const height = local.Height.value === "100%" ? undefined : (parseFloat(local.Height.value) || chartConfig.chart.height)
      chartConfig.chart = {
        ...chartConfig.chart,
        // remove px, % or em from height if present
        height: height,
      };
    }

    return chartConfig;
  });

  const { chart } = chartComponentRef.current || {};

  chart?.redraw();
  const HighchartsReact = HighchartsReactModule.value;
  const Highcharts = HighchartsModule.value;
  return (
    <>
      {local.Loading.value && <div>Loading...</div>}
      {local.Visible.value && modulesLoaded.value && (
        <HighchartsReact
          highcharts={Highcharts}
          options={options.value}
          ref={chartComponentRef}
          containerProps={{
            style: {
              maxHeight: "100%",
              height: "100%",
              minHeight: "100px",
              minWidth: "100px",
              flex: "1",
              width: "100%",
              touchAction: "auto",
            },
            "data-testid": testId,
            "data-type": config.__typename,
          }}
        />
      )}
    </>
  );
}
